/**
 * Role-based Field Permissions Utility
 * 
 * This utility handles role-based field permissions for MainDetails and other components.
 * It determines field editability based on user roles (TJ vs VENDOR).
 */

import { Role, UserDetails } from "@/context/data.types";
import { ACCESS_KEYS } from "@/utils/roleBasedAccessConstants";

export type UserRole = "TJ" | "VENDOR";

export const BUSINESS_ADMIN_ROLE = "Business Admin";
export const CATEGORY_MANAGER_ROLE = "Category Manager";
export const PRODUCT_CREATOR_ROLE = "Product Creator";
export const NUTRITIONIST_ROLE = "Nutritionist";
export const FOOD_SAFETY_TEAM_ROLE = "Food Safety Team";
export const TRAFFIC_MANAGER_ROLE = "Traffic Manager";
export const SYSTEM_EXCELLENCE_MANAGER_ROLE = "System Excellence Manager";
export const PROJECT_MANAGER_ROLE = "Project Manager";
export const PRODUCT_PROCESS_MANAGER_ROLE = "Product Process Manager";

/**
 * Normalize specification type to match permission keys
 * Maps full specification type names to their expected permission keys
 * @param specificationType - Full specification type (e.g., "HABA (Health and Beauty Aids)")
 * @returns Normalized key for permissions lookup (e.g., "HABA")
 */
export const normalizeSpecificationType = (specificationType?: string): string | undefined => {
  if (!specificationType) return undefined;
  
  const normalizedType = specificationType.toLowerCase().trim();
  
  // Map full specification type names to their permission keys
  if (normalizedType.includes('haba') || normalizedType.includes('health and beauty')) {
    return 'HABA';
  }
  if (normalizedType.includes('food') && normalizedType.includes('beverages')) {
    return 'Food & Beverages';
  }
  if (normalizedType.includes('pet foods') || normalizedType.includes('pet food')) {
    return 'Pet Foods';
  }
  if (normalizedType.includes('all branded') || normalizedType.includes('branded products')) {
    return 'All Branded Products';
  }
  if (normalizedType.includes('produce rac')) {
    return 'Produce RAC';
  }

  // If no mapping found, return the original (might be already in correct format)
  return specificationType;
};

/**
 * Get the primary user role from userDetails
 * @param userDetails - User details object containing type
 * @returns Primary user role (TJ or VENDOR)
 */
export const getUserRole = (userDetails: UserDetails | null): UserRole => {
  if (!userDetails?.type) {
    return "VENDOR"; // Default to VENDOR if no type found
  }

  // Use userDetails.type directly - it should be "TJ" or "VENDOR"
  return userDetails.type.toUpperCase() as UserRole;
};

/**
 * Get field permission based on user role and specification type
 * @param permissions - Permission object with role-based access
 * @param userRole - User's role (TJ or VENDOR)
 * @param specificationType - Product specification type (e.g., "Food & Beverages", "HABA", etc.)
 * @returns Field permission object
 */
export const getFieldPermissionByRole = (
  permissions: any,
  userRole: UserRole,
  specificationType?: string
): { isEditable: boolean; isMandatory: boolean } => {
  if (!permissions) {
    return { isEditable: false, isMandatory: false };
  }

  const specificationTypes = [
    "Food & Beverages",
    "HABA",
    "Pet Foods",
    "All Branded Products",
    "Produce RAC",
  ];
  
  // Handle specification-type + role-based permissions (new nested structure)
  if (permissions.isEditable && typeof permissions.isEditable === 'object') {
    
    // Check if this uses the new nested structure (specification type as keys)
    const hasNestedStructure = specificationTypes.some(type => 
      permissions.isEditable.hasOwnProperty(type)
    );
    
    if (hasNestedStructure && specificationType) {
      // New nested structure: permissions.isEditable["Food & Beverages"]["TJ"]
      // Normalize the specification type to match permission keys
      const normalizedSpecType = normalizeSpecificationType(specificationType);
      const specTypePermissions = permissions.isEditable[normalizedSpecType || specificationType];
      
      // Handle role-based mandatory fields (same structure as isEditable)
      let isMandatory = false;
      if (permissions.isMandatory && typeof permissions.isMandatory === 'object') {
        // Check if isMandatory also uses nested structure
        const mandatoryHasNestedStructure = specificationTypes.some(type => 
          permissions.isMandatory.hasOwnProperty(type)
        );
        
        if (mandatoryHasNestedStructure) {
          const specTypeMandatoryPermissions = permissions.isMandatory[normalizedSpecType || specificationType];
          if (specTypeMandatoryPermissions && typeof specTypeMandatoryPermissions === 'object') {
            isMandatory = specTypeMandatoryPermissions[userRole] || false;
          }
        } else {
          // Old structure: permissions.isMandatory["TJ"]
          isMandatory = permissions.isMandatory[userRole] || false;
        }
      } else {
        // Static boolean value for isMandatory
        isMandatory = permissions.isMandatory || false;
      }
      
      if (specTypePermissions && typeof specTypePermissions === 'object') {
        return {
          isEditable: specTypePermissions[userRole] || false,
          isMandatory
        };
      }
      // If specification type not found, default to false for safety
      return {
        isEditable: false,
        isMandatory
      };
    } else {
      // Old structure: permissions.isEditable["TJ"] (for backward compatibility)
      let isMandatory = false;
      if (permissions.isMandatory && typeof permissions.isMandatory === 'object') {
        isMandatory = permissions.isMandatory[userRole] || false;
      } else {
        isMandatory = permissions.isMandatory || false;
      }
      
      return {
        isEditable: permissions.isEditable[userRole] || false,
        isMandatory
      };
    }
  }

  // Handle static permissions (fallback)
  return {
    isEditable: permissions.isEditable || false,
    isMandatory: permissions.isMandatory || false
  };
};

/**
 * Get permissions for a specific field in a specific section
 * @param sectionPermissions - Section permissions object
 * @param fieldName - Field name
 * @param userRole - User's role
 * @param specificationType - Product specification type
 * @returns Field permission object
 */
export const getSectionFieldPermission = (
  sectionPermissions: any,
  fieldName: string,
  userRole: UserRole,
  specificationType?: string
): { isEditable: boolean; isMandatory: boolean } => {
  const fieldPermissions = sectionPermissions?.[fieldName];
  return getFieldPermissionByRole(fieldPermissions, userRole, specificationType);
};

/**
 * Create a permission checker function for a specific section
 * @param sectionPermissions - Section permissions object
 * @param userRole - User's role
 * @param specificationType - Product specification type
 * @returns Function that checks field permissions
 */
export const createPermissionChecker = (
  sectionPermissions: any,
  userRole: UserRole,
  specificationType?: string
) => {
  return (fieldName: string) => {
    return getSectionFieldPermission(sectionPermissions, fieldName, userRole, specificationType);
  };
};

/**
 * Get the priority role state - Business Admin takes precedence over reassignment roles
 * Category Manager and other reassignment-enabled roles have the same functionality
 * @param userRoles - Array of user roles
 * @returns "business admin" | "reassignment role" | null
 */
export const getPriorityRoleState = (userRoles: any[]): string | null => {
  if (!userRoles || userRoles.length === 0) {
    return null;
  }

  // Check for Business Admin first (highest priority)
  const isBusinessAdmin = userRoles.some((role: any) => 
    role?.rolename?.toLowerCase() === 'business admin'
  );
  
  if (isBusinessAdmin) {
    return 'business admin';
  }

  // Check for Category Manager and other roles with same functionality (reassignment enabled)
  const hasReassigningRole = userRoles.some((role: any) => {
    const roleName = role?.rolename?.toLowerCase();
    return roleName === 'category manager' ||
           roleName === 'product creator' ||
           roleName === 'nutritionist' ||
           roleName === 'food safety team' ||
           roleName === 'traffic manager' ||
           roleName === 'system excellence manager' ||
           roleName === 'project manager' ||
           roleName === 'product process manager' ||
           roleName === 'general legal counsel' ||
           roleName === 'organic review specialist' ||
           roleName === 'vendor champion' ||
           roleName === 'vendor user';
  });
  
  if (hasReassigningRole) {
    return 'reassignment role';
  }

  // Return null if neither role is found
  return null;
};

/**
 * Normalize a token `rolename` for comparison: first `|` segment, trim, lowercase.
 * Example: `"Product Creator|..."` → `"product creator"`.
 */
export function normalizeTokenRoleName(rolename: string | null | undefined): string {
  if (rolename == null || !String(rolename).trim()) return "";
  const raw = String(rolename);
  const segment = raw.includes("|") ? (raw.split("|")[0] ?? raw) : raw;
  return segment.trim().toLowerCase();
}

/**
 * True when any session role's `rolename` matches one of `allowedRoleNames`
 * (case-insensitive; compares first `|` segment only).
 *
 * @param roles - Token roles (`userDetails.roles` from `/token` → `iAm.roles`)
 * @param allowedRoleNames - Display names to allow (e.g. `"Product Creator"`)
 */
export function userHasAnyAllowedRoleName(
  roles: ReadonlyArray<{ rolename?: string }> | null | undefined,
  allowedRoleNames: readonly string[]
): boolean {
  if (!roles?.length || !allowedRoleNames.length) return false;

  const wanted = new Set(
    allowedRoleNames.map((name) => normalizeTokenRoleName(name)).filter(Boolean)
  );
  if (wanted.size === 0) return false;

  return roles.some((role) => wanted.has(normalizeTokenRoleName(role?.rolename)));
}

/** Roles allowed to add vendor users and save in the Vendor User Management modal. */
export const VENDOR_USER_MANAGEMENT_SAVE_ROLE_NAMES = [
  "Product Creator",
  "Category Manager",
  "Business Admin",
  "IT Admin",
  "Vendor Champion",
] as const;

/** Token-role check for Vendor User Management add / save actions. */
export function hasVendorUserManagementSaveRole(
  roles: ReadonlyArray<Role> | null | undefined
): boolean {
  return userHasAnyAllowedRoleName(roles, VENDOR_USER_MANAGEMENT_SAVE_ROLE_NAMES);
}

/** True if the session roles include Vendor Champion (token role-name check). */
export function userHasVendorChampionRole(
  roles: ReadonlyArray<Role> | null | undefined
): boolean {
  return userHasAnyAllowedRoleName(roles, ["Vendor Champion"]);
}

/**
 * Add New User toolbar: allowed token role **and** IAM Create/Submit (from caller).
 */
export function canVendorUserManagementAddUser(
  roles: ReadonlyArray<Role> | null | undefined,
  featurePermissions: Record<string, boolean>
): boolean {
  return (
    hasVendorUserManagementSaveRole(roles) &&
    Boolean(featurePermissions[ACCESS_KEYS.CREATE_OR_SUBMIT])
  );
}

/**
 * Modal Add Vendor / Save: allowed token role **and** IAM access (from caller).
 * Create mode → Create/Submit; edit mode → Update.
 */
export function canVendorUserManagementSaveInModal(
  roles: ReadonlyArray<Role> | null | undefined,
  isEditMode: boolean,
  featurePermissions: Record<string, boolean>
): boolean {
  const accessKey = isEditMode ? ACCESS_KEYS.UPDATE : ACCESS_KEYS.CREATE_OR_SUBMIT;
  return hasVendorUserManagementSaveRole(roles) && Boolean(featurePermissions[accessKey]);
}